import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, Heading, Spinner, Text, Select, Button, Input, useToast, Stack, FormControl, FormLabel, Checkbox } from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import Axios from '../utils/httpClient';
import { kpiSections } from './kpiData';
import { API_URL } from '../utils/api';
import Layout from './Layout';

// Parameters that should be handled through kafedra scoring
const PARAM_CODES = ['1.2', '1.3', '1.5', '2.1', '2.2', '2.3', '3.1', '3.2', '4.7'];

const KafedraPointDistribution = () => {
  console.log('🚀 KafedraPointDistribution component rendering');

  const { id } = useParams(); // parameter code
  console.log('🔍 URL param id:', id);

  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [kafedralar, setKafedralar] = useState([]);
  const [selectedKafedra, setSelectedKafedra] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [points, setPoints] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [awardedTeachers, setAwardedTeachers] = useState([]); // Track teachers who already received points
  const [awardedPoints, setAwardedPoints] = useState({}); // Track the points awarded to each teacher

  const param = useMemo(() => {
    console.log('🔍 KafedraPointDistribution - param useMemo called with id:', id);
    try {
      console.log('🔍 kpiSections:', kpiSections);
      console.log('🔍 kpiSections type:', typeof kpiSections);
      console.log('🔍 kpiSections isArray:', Array.isArray(kpiSections));

      if (!kpiSections || !Array.isArray(kpiSections)) {
        console.error('❌ kpiSections is not an array:', kpiSections);
        return null;
      }

      const all = kpiSections.flatMap(s => {
        console.log('🔍 Processing section:', s);
        return s?.items || [];
      });
      console.log('🔍 All parameters:', all);
      console.log('🔍 Looking for parameter with id:', id);

      const found = all.find(p => {
        const match = String(p?.no) === String(id);
        console.log(`🔍 Checking param ${p?.no} === ${id}? ${match}`);
        return match;
      });

      if (!found) {
        console.warn('⚠️ Parameter not found for id:', id);
        console.warn('⚠️ Available parameter IDs:', all.map(p => p?.no));
      } else {
        console.log('✅ Found parameter:', found);
      }

      return found || null;
    } catch (error) {
      console.error('❌ Error in param useMemo:', error);
      console.error('❌ Error stack:', error.stack);
      return null;
    }
  }, [id]);

  // Load kafedralar
  useEffect(() => {
    setLoading(true);
    Axios().get('api/v1/account/kafedralar/')
      .then(res => setKafedralar(res?.data?.kafedralar || []))
      .catch(e => {
        toast({ title: 'Xatolik yuz berdi', status: 'error' });
        console.error(e);
      })
      .finally(() => setLoading(false));
  }, []);

  // Load teachers when kafedra is selected
  useEffect(() => {
    console.log('🔍 useEffect triggered - selectedKafedra:', selectedKafedra, 'id:', id);

    if (!selectedKafedra) {
      console.log('🔍 No kafedra selected, clearing data');
      setTeachers([]);
      setSelectedTeachers([]);
      setAwardedTeachers([]);
      return;
    }

    console.log('🔍 Loading teachers for kafedra:', selectedKafedra);
    setLoading(true);

    Promise.all([
      Axios().get(`api/v1/account/kafedralar/${selectedKafedra}/teachers/`),
      // Load awarded teachers for this parameter and kafedra
      Axios().get(`api/v1/account/kafedra/${selectedKafedra}/awarded-teachers/`, {
        params: { parameter_code: id }
      }).catch(err => {
        // If awarded-teachers endpoint fails, return empty array
        console.warn('⚠️ Failed to load awarded teachers:', err);
        console.warn('⚠️ Error response:', err?.response);
        return { data: { awarded_teacher_ids: [], awarded_points: {} } };
      })
    ])
      .then(([teachersRes, appsRes]) => {
        console.log('✅ Teachers response:', teachersRes);
        console.log('✅ Awarded teachers response:', appsRes);

        const teachers = teachersRes?.data?.teachers || [];
        const awarded = appsRes?.data?.awarded_teacher_ids || [];
        const awardedPointsData = appsRes?.data?.awarded_points || {};

        console.log('✅ Setting teachers:', teachers);
        console.log('✅ Setting awarded teachers:', awarded);
        console.log('✅ Setting awarded points:', awardedPointsData);

        setTeachers(teachers);
        setAwardedTeachers(awarded);
        setAwardedPoints(awardedPointsData);
        
        // Pre-fill points state with awarded data for KPI 1.2 and 1.5
        if (String(id) === '1.2' || String(id) === '1.5') {
          const initialPoints = {};
          awarded.forEach(teacherId => {
            if (String(id) === '1.2') {
              const percentage = awardedPointsData[`${teacherId}_percentage`];
              const ball = awardedPointsData[teacherId];
              if (percentage !== undefined) {
                initialPoints[teacherId] = { percentage, ball };
              }
            } else if (String(id) === '1.5') {
              const grade = awardedPointsData[`${teacherId}_grade`];
              const ball = awardedPointsData[teacherId];
              if (grade !== undefined) {
                initialPoints[teacherId] = { grade, ball };
              }
            }
          });
          setPoints(initialPoints);
          console.log('✅ Pre-filled points:', initialPoints);
        }
      })
      .catch(e => {
        console.error('❌ Error loading teachers:', e);
        console.error('❌ Error response:', e?.response);
        console.error('❌ Error data:', e?.response?.data);

        toast({ title: 'Xatolik yuz berdi', status: 'error' });
        // Set empty arrays to prevent crashes
        setTeachers([]);
        setAwardedTeachers([]);
      })
      .finally(() => {
        console.log('🔍 Loading complete');
        setLoading(false);
      });
  }, [selectedKafedra, id]);

  const handleTeacherSelect = (teacherId) => {
    setSelectedTeachers(prev => {
      if (prev.includes(teacherId)) {
        // Deselecting - remove points
        setPoints(prevPoints => {
          const newPoints = { ...prevPoints };
          delete newPoints[teacherId];
          return newPoints;
        });
        return prev.filter(id => id !== teacherId);
      } else {
        // Selecting - auto-assign max points for KPI 3.1, 3.2, and 4.7
        if ((String(id) === '3.1' || String(id) === '3.2' || String(id) === '4.7') && param) {
          const maxBall = parseInt(String(param.maxBall || '').replace(/\D/g, '') || '2', 10);
          setPoints(prevPoints => ({
            ...prevPoints,
            [teacherId]: maxBall.toString()
          }));
        }
        return [...prev, teacherId];
      }
    });
  };

  const handlePointChange = (teacherId, value) => {
    setPoints(prev => ({
      ...prev,
      [teacherId]: value
    }));
  };

  const submitPoints = () => {
    if (selectedTeachers.length === 0) {
      toast({ title: 'Hech kim tanlanmadi', status: 'warning' });
      return;
    }

    console.log('=== SUBMIT POINTS DEBUG ===');
    console.log('Parameter code:', id);
    console.log('Selected teachers:', selectedTeachers);
    console.log('Points state:', points);
    console.log('=========================');

    setSubmitting(true);

    // Prepare assignments for batch submission
    const assignments = selectedTeachers.map(teacherId => {
      let ball = 0;
      let percentage = null;
      let grade = null;

      // For KPI 1.2, extract ball and percentage from the object structure
      if (String(id) === '1.2') {
        const pointData = points[teacherId];
        if (pointData && typeof pointData === 'object') {
          ball = parseInt(pointData.ball || 0, 10);
          percentage = pointData.percentage;
        }
      }
      // For KPI 1.5, extract ball and grade from the object structure
      else if (String(id) === '1.5') {
        const pointData = points[teacherId];
        console.log('KPI 1.5 submit - pointData for teacher', teacherId, ':', pointData);
        console.log('KPI 1.5 submit - pointData type:', typeof pointData);
        console.log('KPI 1.5 submit - pointData.ball:', pointData?.ball, 'type:', typeof pointData?.ball);
        console.log('KPI 1.5 submit - pointData.grade:', pointData?.grade, 'type:', typeof pointData?.grade);
        
        if (pointData && typeof pointData === 'object') {
          // Make sure ball is a valid number
          const ballValue = pointData.ball;
          if (typeof ballValue === 'number' && ballValue > 0) {
            ball = ballValue;
          } else if (typeof ballValue === 'string') {
            ball = parseInt(ballValue, 10);
          } else {
            console.error('KPI 1.5: Invalid ball value:', ballValue);
            ball = 0;
          }
          
          grade = pointData.grade;
          console.log('KPI 1.5 submit - extracted:', { ball, grade });
        } else {
          console.error('KPI 1.5: pointData is not an object or is null:', pointData);
        }
      }
      else {
        // For other parameters, points is stored as a simple value
        ball = parseInt(points[teacherId] || 0, 10);
      }

      // For parameter 3.1, 3.2, and 4.7, automatically assign maximum points if no specific points are selected
      // Note: 2.1 now uses explicit point selection (4 or 2)
      if ((String(id) === '3.1' || String(id) === '3.2' || String(id) === '4.7') && ball === 0) {
        ball = parseInt(String(param.maxBall || '').replace(/\D/g, '') || '2', 10);
      }

      return {
        teacher_id: teacherId,
        ball: ball,
        parameter_code: id,
        percentage: percentage,  // Include percentage for KPI 1.2
        grade: grade  // Include grade for KPI 1.5
      };
    }).filter(assignment => assignment.ball > 0); // Only include valid assignments

    if (assignments.length === 0) {
      toast({ title: 'Hech qanday ball berilmadi', status: 'warning' });
      setSubmitting(false);
      return;
    }

    // Submit batch request
    const endpoint = 'api/v1/account/kafedra/batch-give-point/';
    console.log('🚀 Submitting to endpoint:', endpoint);
    console.log('🚀 Assignments:', assignments);
    console.log('🚀 Assignments JSON:', JSON.stringify(assignments, null, 2));
    console.log('🚀 Full URL will be:', `${API_URL}/${endpoint}`);

    Axios().post(endpoint, { assignments })
      .then(() => {
        console.log('✅ Submission successful!');
        toast({ title: 'Ball berildi', status: 'success' });
        // Clear selected teachers but keep points data
        setSelectedTeachers([]);
        // Don't clear points - keep them visible in inputs
        // Refresh awarded teachers list
        if (selectedKafedra) {
          Axios().get(`api/v1/account/kafedra/${selectedKafedra}/awarded-teachers/`, {
            params: { parameter_code: id }
          }).then(res => {
            const awarded = res?.data?.awarded_teacher_ids || [];
            const awardedPointsData = res?.data?.awarded_points || {};
            setAwardedTeachers(awarded);
            setAwardedPoints(awardedPointsData);
            
            // Update points state with the new awarded data for KPI 1.2 and 1.5
            if (String(id) === '1.2' || String(id) === '1.5') {
              setPoints(prevPoints => {
                const updatedPoints = { ...prevPoints };
                awarded.forEach(teacherId => {
                  if (String(id) === '1.2') {
                    const percentage = awardedPointsData[`${teacherId}_percentage`];
                    const ball = awardedPointsData[teacherId];
                    if (percentage !== undefined) {
                      updatedPoints[teacherId] = { percentage, ball };
                    }
                  } else if (String(id) === '1.5') {
                    const grade = awardedPointsData[`${teacherId}_grade`];
                    const ball = awardedPointsData[teacherId];
                    if (grade !== undefined) {
                      updatedPoints[teacherId] = { grade, ball };
                    }
                  }
                });
                return updatedPoints;
              });
            }
          }).catch(err => {
            console.warn('Failed to refresh awarded teachers:', err);
            // Don't show error to user, just log it
          });
        }
      })
      .catch(e => {
        toast({ title: e?.response?.data?.detail || 'Xatolik yuz berdi', status: 'error' });
        console.error(e);
      })
      .finally(() => setSubmitting(false));
  };

  console.log('🔍 Render check - param:', param);
  console.log('🔍 Render check - param.no:', param?.no);
  console.log('🔍 Render check - PARAM_CODES:', PARAM_CODES);
  console.log('🔍 Render check - includes?', param?.no ? PARAM_CODES.includes(String(param.no)) : false);

  if (!param || !param.no || !PARAM_CODES.includes(String(param.no))) {
    console.log('⚠️ Showing error: param not found or not in PARAM_CODES');
    return (
      <Layout>
        <Box p={4}>
          <Text>Parametr topilmadi yoki kafedra orqali baholanmaydi</Text>
          {id && <Text fontSize="sm" color="gray.600" mt={2}>ID: {id}</Text>}
          <Text fontSize="sm" color="gray.600" mt={2}>param: {JSON.stringify(param)}</Text>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box p={4}>
        <Heading size='md' mb={3}>{param?.no || id} — {param?.parameter || 'Parametr'}</Heading>
        <Box display='flex' gap={3} alignItems='center' mb={3}>
          <Button
            size='sm'
            leftIcon={<ArrowBackIcon />}
            bg='#133654'
            color='white'
            borderRadius='10px'
            _hover={{ bg: '#0f2a42' }}
            onClick={() => navigate(-1)}
          >
            Orqaga
          </Button>
        </Box>

        <Card p={4} mb={4}>
          <Text fontWeight={600} mb={2}>Mexanizm</Text>
          <Text whiteSpace='pre-wrap'>{param?.mechanism || ''}</Text>
          <Text mt={3}><b>Maks. ball:</b> {param?.maxBall || 'N/A'}</Text>
        </Card>

        {loading ? (
          <Spinner />
        ) : (
          <>
            <Card p={4} mb={4}>
              <FormControl mb={4}>
                <FormLabel>Kafedra tanlang</FormLabel>
                <Select
                  placeholder="Kafedra tanlang"
                  value={selectedKafedra}
                  onChange={e => setSelectedKafedra(e.target.value)}
                >
                  {kafedralar.map(k => (
                    <option key={k.id} value={k.id}>{k.name}</option>
                  ))}
                </Select>
              </FormControl>

              {selectedKafedra && teachers.length > 0 && (
                <>
                  <Box mb={4}>
                    <Text fontWeight={600} mb={2}>O'qituvchilar</Text>
                    
                    {/* Header row for KPI 2.1 */}
                    {String(id) === '2.1' && (
                      <Box 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center"
                        p={2}
                        bg="gray.100"
                        borderRadius="md"
                        mb={2}
                      >
                        <Text fontWeight={600} flex="1">Ism</Text>
                        <Box display="flex" gap={6}>
                          <Text fontWeight={600} minW="100px" textAlign="center">Fan doktori</Text>
                          <Text fontWeight={600} minW="100px" textAlign="center" lineHeight="1.3">
                            Fan nomzodi <br />Falsafa doktori
                          </Text>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Header row for KPI 2.2 */}
                    {String(id) === '2.2' && (
                      <Box 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center"
                        p={2}
                        bg="gray.100"
                        borderRadius="md"
                        mb={2}
                      >
                        <Text fontWeight={600} flex="1">Ism</Text>
                        <Box display="flex" gap={6}>
                          <Text fontWeight={600} minW="100px" textAlign="center">Professorlik</Text>
                          <Text fontWeight={600} minW="100px" textAlign="center">Dotsentlik</Text>
                        </Box>
                      </Box>
                    )}
                    
                    <Stack spacing={2}>
                      {teachers.map(t => {
                        console.log('🔍 Rendering teacher:', t);
                        if (!t || !t.teacher_id) {
                          console.error('❌ Invalid teacher object:', t);
                          return null;
                        }
                        const isAwarded = awardedTeachers.includes(t.teacher_id);
                        return (
                          <Box
                            key={t.teacher_id}
                            p={3}
                            borderWidth="1px"
                            borderRadius="md"
                            bg={isAwarded ? 'green.50' : 'white'}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box flex="1">
                              <Text fontWeight={500}>{t.teacher_name}</Text>
                              {isAwarded && (
                                <Text fontSize="sm" color="green.600" fontWeight="bold">✓ Ball berilgan</Text>
                              )}
                            </Box>

                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                                {/* KPI 1.2: Percentage input - always editable */}
                                {String(id) === '1.2' && (
                                  <Box>
                                    <Input
                                      size='sm'
                                      w='80px'
                                      type="number"
                                      min="0"
                                      max="100"
                                      placeholder="%"
                                      value={
                                        points[t.teacher_id]?.percentage !== undefined 
                                          ? points[t.teacher_id]?.percentage 
                                          : (isAwarded ? awardedPoints[`${t.teacher_id}_percentage`] || '' : '')
                                      }
                                      onChange={e => {
                                        const percentage = parseFloat(e.target.value) || 0;
                                        let calculatedPoints = 0;
                                        if (percentage >= 90) calculatedPoints = 8;
                                        else if (percentage >= 60) calculatedPoints = 6;
                                        else if (percentage >= 30) calculatedPoints = 3;
                                        handlePointChange(t.teacher_id, { percentage, ball: calculatedPoints });
                                        if (!selectedTeachers.includes(t.teacher_id)) {
                                          setSelectedTeachers(prev => [...prev, t.teacher_id]);
                                        }
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* KPI 1.5: Grade input - always editable */}
                                {String(id) === '1.5' && (
                                  <Box>
                                    <Input
                                      size='sm'
                                      w='80px'
                                      type="number"
                                      min="1.0"
                                      max="3.0"
                                      step="0.1"
                                      placeholder="Baho"
                                      lang="en"
                                      value={
                                        points[t.teacher_id]?.grade !== undefined 
                                          ? points[t.teacher_id]?.grade 
                                          : (isAwarded ? awardedPoints[`${t.teacher_id}_grade`] || '' : '')
                                      }
                                      onChange={e => {
                                        const gradeValue = e.target.value;
                                        const grade = parseFloat(gradeValue);
                                        
                                        // Only process if grade is a valid number
                                        if (!isNaN(grade) && grade >= 1.0 && grade <= 3.0) {
                                          let calculatedPoints = 0;
                                          // O'rtacha baho 2.5-3 bo'lsa 4 ball
                                          if (grade >= 2.5 && grade <= 3.0) {
                                            calculatedPoints = 4;
                                          }
                                          // O'rtacha baho 2-2.5 bo'lsa 2 ball
                                          else if (grade >= 2.0 && grade < 2.5) {
                                            calculatedPoints = 2;
                                          }
                                          // O'rtacha baho 1-2 bo'lsa 1 ball
                                          else if (grade >= 1.0 && grade < 2.0) {
                                            calculatedPoints = 1;
                                          }
                                          
                                          console.log('KPI 1.5 grade input:', { grade, calculatedPoints });
                                          handlePointChange(t.teacher_id, { grade, ball: calculatedPoints });
                                          
                                          if (!selectedTeachers.includes(t.teacher_id)) {
                                            setSelectedTeachers(prev => [...prev, t.teacher_id]);
                                          }
                                        } else if (gradeValue === '') {
                                          // Clear the value if input is empty
                                          handlePointChange(t.teacher_id, { grade: '', ball: 0 });
                                          setSelectedTeachers(prev => prev.filter(id => id !== t.teacher_id));
                                        }
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* KPI 2.1: Two checkboxes for different degrees */}
                                {String(id) === '2.1' && (
                                  <Box display="flex" gap={6} alignItems="center">
                                    <Box minW="100px" display="flex" justifyContent="center">
                                      <Checkbox
                                        isChecked={
                                          points[t.teacher_id] === '4' || 
                                          (points[t.teacher_id] === undefined && isAwarded && String(awardedPoints[t.teacher_id]) === '4')
                                        }
                                        onChange={() => {
                                          const currentValue = points[t.teacher_id] || (isAwarded ? String(awardedPoints[t.teacher_id]) : '');
                                          if (currentValue === '4') {
                                            // Uncheck
                                            handlePointChange(t.teacher_id, '');
                                            setSelectedTeachers(prev => prev.filter(id => id !== t.teacher_id));
                                          } else {
                                            // Check and set 4 points
                                            handlePointChange(t.teacher_id, '4');
                                            if (!selectedTeachers.includes(t.teacher_id)) {
                                              setSelectedTeachers(prev => [...prev, t.teacher_id]);
                                            }
                                          }
                                        }}
                                      />
                                    </Box>
                                    <Box minW="100px" display="flex" justifyContent="center">
                                      <Checkbox
                                        isChecked={
                                          points[t.teacher_id] === '2' || 
                                          (points[t.teacher_id] === undefined && isAwarded && String(awardedPoints[t.teacher_id]) === '2')
                                        }
                                        onChange={() => {
                                          const currentValue = points[t.teacher_id] || (isAwarded ? String(awardedPoints[t.teacher_id]) : '');
                                          if (currentValue === '2') {
                                            // Uncheck
                                            handlePointChange(t.teacher_id, '');
                                            setSelectedTeachers(prev => prev.filter(id => id !== t.teacher_id));
                                          } else {
                                            // Check and set 2 points
                                            handlePointChange(t.teacher_id, '2');
                                            if (!selectedTeachers.includes(t.teacher_id)) {
                                              setSelectedTeachers(prev => [...prev, t.teacher_id]);
                                            }
                                          }
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                )}

                                {/* KPI 3.1, 3.2, 4.7: Auto max points */}
                                {(String(id) === '3.1' || String(id) === '3.2' || String(id) === '4.7') && !isAwarded && (
                                  <Checkbox
                                    isChecked={selectedTeachers.includes(t.teacher_id)}
                                    onChange={() => handleTeacherSelect(t.teacher_id)}
                                  />
                                )}

                                {/* KPI 2.2: Two checkboxes for Professor/Dotsent */}
                                {String(id) === '2.2' && (
                                  <Box display="flex" gap={6} alignItems="center">
                                    <Box minW="100px" display="flex" justifyContent="center">
                                      <Checkbox
                                        isChecked={
                                          points[t.teacher_id] === '3' || 
                                          (points[t.teacher_id] === undefined && isAwarded && String(awardedPoints[t.teacher_id]) === '3')
                                        }
                                        onChange={() => {
                                          const currentValue = points[t.teacher_id] || (isAwarded ? String(awardedPoints[t.teacher_id]) : '');
                                          if (currentValue === '3') {
                                            // Uncheck
                                            handlePointChange(t.teacher_id, '');
                                            setSelectedTeachers(prev => prev.filter(id => id !== t.teacher_id));
                                          } else {
                                            // Check and set 3 points
                                            handlePointChange(t.teacher_id, '3');
                                            if (!selectedTeachers.includes(t.teacher_id)) {
                                              setSelectedTeachers(prev => [...prev, t.teacher_id]);
                                            }
                                          }
                                        }}
                                      />
                                    </Box>
                                    <Box minW="100px" display="flex" justifyContent="center">
                                      <Checkbox
                                        isChecked={
                                          points[t.teacher_id] === '2' || 
                                          (points[t.teacher_id] === undefined && isAwarded && String(awardedPoints[t.teacher_id]) === '2')
                                        }
                                        onChange={() => {
                                          const currentValue = points[t.teacher_id] || (isAwarded ? String(awardedPoints[t.teacher_id]) : '');
                                          if (currentValue === '2') {
                                            // Uncheck
                                            handlePointChange(t.teacher_id, '');
                                            setSelectedTeachers(prev => prev.filter(id => id !== t.teacher_id));
                                          } else {
                                            // Check and set 2 points
                                            handlePointChange(t.teacher_id, '2');
                                            if (!selectedTeachers.includes(t.teacher_id)) {
                                              setSelectedTeachers(prev => [...prev, t.teacher_id]);
                                            }
                                          }
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                )}

                                {/* Other KPIs: Select dropdown */}
                                {String(id) !== '2.1' && String(id) !== '2.2' && String(id) !== '3.1' && String(id) !== '3.2' && String(id) !== '4.7' && String(id) !== '1.2' && String(id) !== '1.5' && !isAwarded && (
                                  <Select
                                    size='sm'
                                    w='80px'
                                    value={points[t.teacher_id] || ''}
                                    onChange={e => {
                                      handlePointChange(t.teacher_id, e.target.value);
                                      if (!selectedTeachers.includes(t.teacher_id)) {
                                        setSelectedTeachers(prev => [...prev, t.teacher_id]);
                                      }
                                    }}
                                  >
                                    <option value="">-</option>
                                    {Array.from({ length: parseInt(String(param?.maxBall || '').replace(/\D/g, '') || '5', 10) }, (_, i) => i + 1).map(n => (
                                      <option key={n} value={n}>{n}</option>
                                    ))}
                                  </Select>
                                )}
                              </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>

                  {selectedTeachers.length > 0 && (
                    <Button
                      colorScheme='whatsapp'
                      onClick={submitPoints}
                      isLoading={submitting}
                    >
                      {String(id) === '2.1' ? 'Tanlanganlarni tasdiqlash' : 'Tanlanganlarga ball berish'}
                    </Button>
                  )}
                </>
              )}
            </Card>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default KafedraPointDistribution;